import React, { useMemo } from 'react';

// ── Constants ────────────────────────────────────────────────────────────────
const HEX_SIZE = 46; // Pixel size of each hexagon (circumradius)

// Team color mappings
const TEAM_COLORS = {
  orange: '#FF8C00',
  green:  '#2E8B57',
  blue:   '#1565C0',
  white:  '#FAFAF8',
};

const TEAM_STROKE = {
  orange: '#CC6600',
  green:  '#1B6B40',
  blue:   '#0D47A1',
  white:  '#aab0c0',
};

// ── Hex Math ─────────────────────────────────────────────────────────────────
// Pointy-top hexagon: x = size*(√3*q + √3/2*r), y = size*(3/2*r)
function hexToPixel(q, r, size) {
  const x = size * (Math.sqrt(3) * q + Math.sqrt(3) / 2 * r);
  const y = size * (3 / 2 * r);
  return { x, y };
}

function hexCorners(cx, cy, size) {
  const points = [];
  for (let i = 0; i < 6; i++) {
    const angleDeg = 60 * i - 30; // pointy-top
    const angleRad = (Math.PI / 180) * angleDeg;
    points.push({
      x: cx + size * Math.cos(angleRad),
      y: cy + size * Math.sin(angleRad),
    });
  }
  return points;
}

function pointsToSVG(pts) {
  return pts.map(p => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ');
}

// Generate all axial coords for hex grid of given radius
function generateHexCoords(radius) {
  const coords = [];
  for (let q = -radius; q <= radius; q++) {
    const r1 = Math.max(-radius, -q - radius);
    const r2 = Math.min(radius, -q + radius);
    for (let r = r1; r <= r2; r++) {
      coords.push({ q, r });
    }
  }
  return coords;
}

// ── Edge detection (matches backend logic) ───────────────────────────────────
function isEdgeCell(q, r, team, radius) {
  if (team === 'orange') return r === -radius || r === radius;
  if (team === 'green')  return (q + r) === radius || (q + r) === -radius;
  if (team === 'blue')   return q === -radius || q === radius;
  return false;
}

// ── HexGrid Component ─────────────────────────────────────────────────────────
export default function HexGrid({ grid, onCellClick, winPath, gridRadius = 3 }) {
  const coords = useMemo(() => generateHexCoords(gridRadius), [gridRadius]);

  // Compute SVG bounding box
  const pixels = useMemo(() => coords.map(({ q, r }) => hexToPixel(q, r, HEX_SIZE)), [coords]);
  const minX = Math.min(...pixels.map(p => p.x)) - HEX_SIZE;
  const maxX = Math.max(...pixels.map(p => p.x)) + HEX_SIZE;
  const minY = Math.min(...pixels.map(p => p.y)) - HEX_SIZE;
  const maxY = Math.max(...pixels.map(p => p.y)) + HEX_SIZE;
  const W = maxX - minX;
  const H = maxY - minY;

  // Board shape logic for drawing solid wedges
  const H_inner = (gridRadius * 1.5 + 0.8) * HEX_SIZE;
  const H_outer = H_inner + 42; // Border thickness
  const R_inner = 0; // Set to 0 so the wedges start exactly from the center, perfectly hugging the hexes!
  const R_outer = H_outer * (2 / Math.sqrt(3));
  
  // 6 edges of the flat-top bounding hexagon
  // Angles in degrees: -120 to -60 (Top), -60 to 0 (Top-Right), etc.
  const wedgeAngles = [-120, -60, 0, 60, 120, 180, 240];
  const borderTeams = [
    'orange', // Top
    'green',  // Top-Right
    'blue',   // Bottom-Right
    'orange', // Bottom
    'green',  // Bottom-Left
    'blue'    // Top-Left
  ];
  
  const borderTexts = [
    'الهدف البرتقالي',
    'الهدف الأخضر',
    'الهدف الأزرق',
    'الهدف البرتقالي',
    'الهدف الأخضر',
    'الهدف الأزرق'
  ];

  const PAD = 28; // padding for border band
  const totalW = W + PAD * 2;
  const totalH = H + PAD * 2;
  const ox = -minX + PAD; // origin offset x
  const oy = -minY + PAD; // origin offset y

  if (!grid) {
    return (
      <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 40 }}>
        جاري تحميل الشبكة...
      </div>
    );
  }

  return (
    <svg
      width={totalW}
      height={totalH}
      viewBox={`0 0 ${totalW} ${totalH}`}
      style={{ display: 'block', maxWidth: '100%' }}
    >
      <defs>
        {/* Glow filter for win path */}
        <filter id="glow">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
        {/* Drop shadow */}
        <filter id="hex-shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="3" stdDeviation="4" floodColor="rgba(0,0,0,0.5)" />
        </filter>
      </defs>

      {/* ── Background solid border wedges ────────────────────────────────── */}
      {borderTeams.map((team, i) => {
        const a1 = wedgeAngles[i] * (Math.PI / 180);
        const a2 = wedgeAngles[i + 1] * (Math.PI / 180);
        
        const p1 = { x: ox + R_inner * Math.cos(a1), y: oy + R_inner * Math.sin(a1) };
        const p2 = { x: ox + R_outer * Math.cos(a1), y: oy + R_outer * Math.sin(a1) };
        const p3 = { x: ox + R_outer * Math.cos(a2), y: oy + R_outer * Math.sin(a2) };
        const p4 = { x: ox + R_inner * Math.cos(a2), y: oy + R_inner * Math.sin(a2) };
        
        const pts = `${p1.x},${p1.y} ${p2.x},${p2.y} ${p3.x},${p3.y} ${p4.x},${p4.y}`;
        
        // Text positioning (keep it near the outer edge)
        const midA = (wedgeAngles[i] + wedgeAngles[i+1]) / 2;
        const midRad = midA * (Math.PI / 180);
        const H_mid = H_inner + 21; // Middle of the border thickness
        const tx = ox + H_mid * Math.cos(midRad);
        const ty = oy + H_mid * Math.sin(midRad);
        // Rotate text to align with edge: angle + 90
        let rot = midA + 90;
        // Keep text upright
        if (rot > 90 && rot < 270) rot -= 180;
        
        return (
          <g key={`wedge-${i}`}>
            <polygon
              points={pts}
              fill={TEAM_COLORS[team]}
              stroke="rgba(0,0,0,0.1)"
              strokeWidth="2"
            />
            <text
              x={tx}
              y={ty + 2} /* slight optical adjustment */
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={14}
              fontWeight="800"
              fontFamily="Tajawal, sans-serif"
              fill="#fff"
              transform={`rotate(${rot}, ${tx}, ${ty})`}
              style={{ pointerEvents: 'none' }}
            >
              {borderTexts[i]}
            </text>
          </g>
        );
      })}

      {/* ── Hexagon cells ────────────────────────────────────────────────── */}
      {coords.map(({ q, r }) => {
        const key = `${q},${r}`;
        const cell = grid[key];
        if (!cell) return null;

        const { x, y } = hexToPixel(q, r, HEX_SIZE);
        const cx = x + ox;
        const cy = y + oy;
        const corners = hexCorners(cx, cy, HEX_SIZE - 2);
        const isWin = winPath && winPath.includes(key);
        const color = cell.color || 'white';

        const fillColor = color === 'white' ? '#1e2235' : TEAM_COLORS[color];
        const strokeColor = isWin ? '#fff' : (color === 'white' ? '#3a4060' : TEAM_STROKE[color]);
        const strokeW = isWin ? 3 : 1.5;

        return (
          <g
            key={key}
            onClick={(e) => onCellClick(e, key, cell)}
            style={{ cursor: 'pointer' }}
            filter={isWin ? 'url(#glow)' : 'url(#hex-shadow)'}
          >
            <polygon
              className={color === 'focus' ? 'hex-focus' : ''}
              points={pointsToSVG(corners)}
              fill={color === 'focus' ? undefined : fillColor}
              stroke={color === 'focus' ? undefined : strokeColor}
              strokeWidth={color === 'focus' ? undefined : strokeW}
              style={{ transition: color === 'focus' ? 'none' : 'fill 0.3s ease, stroke 0.2s ease' }}
            />
            {/* Letter text */}
            <text
              x={cx}
              y={cy + 1}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={HEX_SIZE * 0.52}
              fontWeight="700"
              fontFamily="Tajawal, sans-serif"
              fill={color === 'white' ? '#e8eaf0' : 'rgba(255,255,255,0.92)'}
              style={{ pointerEvents: 'none', userSelect: 'none' }}
            >
              {cell.letter}
            </text>
          </g>
        );
      })}

      {/* Label section removed as text is now in wedges */}
    </svg>
  );
}
